// ------------------------------------------------------------------------------------------------------
import { ReactNode, PureComponent, Component, CSSProperties, ReactElement, lazy, Suspense } from 'react';
import _ from 'lodash';
import { RGBColor } from 'react-color';
import { Highlight } from 'canvas-gauges';
import { arrayMoveImmutable } from '@Utils/Utils';
import { SketchPicker } from 'react-color';
import { BoardTheming } from '@UI/Dashboard/BoardTheming';
import { getZDepthShadow, grey300, grey500, grey600, red200, blue200 } from '@Utils/Colors';
import { isDefined, IsNullOrEmpty, isFunction } from '@Utils/Micro';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { SearchBar } from '@Primitive/SearchBar';
import { DialogConf, ExDialog } from '@Primitive/ExDialog';
import "../../../Primitive/PrimitiveCss.less";
import { DraggableIconStyle } from '@Primitive/Sortable';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { IconButton, Button } from '@Primitive/Button';
import { FlexboxGrid } from 'rsuite';
import TextBox from '@Primitive/TextBox';
import Toolbar from '@Primitive/Toolbar';
import ListItem from '@Primitive/ListItem';
import Popover from '@Primitive/Popover';
import { ToolbarIconInfo_ButtonRender } from '@Primitive/ToolbarIconButton';
import { eIconType } from '@Primitive/SettingsListType';

const Sortable = lazy(() => import("@Primitive/Sortable"));
// ------------------------------------------------------------------------------------------------------
export function getValueFromHighlights(value: number, highlights: GaugeHelperHighlight[]): {
    success: boolean,
    color: string,
    textColor: string
} {
    const result = { success: false, color: '', textColor: '' };

    // if there is any highlight
    if (isDefined(highlights) && highlights.length > 0) {

        // get highlight for knob's value.
        let highlight: GaugeHelperHighlight = highlights.find(highlight => {
            return highlight.from <= value && highlight.to >= value;
        });

        // update result entry found
        if (isDefined(highlight)) {
            result.color = highlight.color;
            result.textColor = highlight.textColor;
            result.success = true;
            return result;
        }
    }
    return result;
}
// ------------------------------------------------------------------------------------------------------
// todo: check if we can remove this function
export function getStringFromRGBA(color: RGBColor): string {
    return `rgba(${color.r},${color.g},${color.b},${color.a})`;
}
// ------------------------------------------------------------------------------------------------------
export function validateHighlights(minValue: number, maxValue: number, highlights: GaugeHelperHighlight[]): boolean {
    return validateHighlightsWithMsg(minValue, maxValue, highlights).valid;
}
// --------------------------------------------------------------------------------------------
export function validateHighlightsWithMsg(
    minValue: number,
    maxValue: number,
    highlights: GaugeHelperHighlight[]): { valid: boolean, errorMsg?: string | ReactNode } {

    // protect against invalid data type
    if (!_.isArray(highlights))
        return {
            valid: false,
            errorMsg: ''
        };

    if (isDefined(minValue) && isDefined(maxValue))
        for (let i = 0; i < highlights.length; i++) {
            const el = highlights[i];

            // check values
            if (!isDefined(el.from) || !isDefined(el.to) || _.isNaN(el.from) || _.isNaN(el.to))
                return {
                    valid: false,
                    errorMsg: "From and To values should be numeric"
                };

            // check ranges
            if (minValue > el.from || minValue > el.to ||
                maxValue < el.from || maxValue < el.to)
                return {
                    valid: false,
                    errorMsg: 'Please check again your From and To fields which should be included betweeen Min and Max values.'
                };

            // check `from` `to`
            if (el.from > el.to)
                return {
                    valid: false,
                    errorMsg: "From values should be lower than To values"
                };
        }
    else
        for (let i = 0; i < highlights.length; i++) {
            const el = highlights[i];

            // check values
            if (!isDefined(el.from) || !isDefined(el.to) || _.isNaN(el.from) || _.isNaN(el.to))
                return {
                    valid: false,
                    errorMsg: "From and To values should be numeric"
                };

            // check `from` `to`
            if (el.from > el.to)
                return {
                    valid: false,
                    errorMsg: "From values should be lower than To values"
                };
        }

    // no error; return true
    return {
        valid: true,
        errorMsg: ''
    };
}
// ---------------------------------------------------------------------------------------------------
export interface HighlightsEditorProps {
    isVisible: boolean;
    enableDecimal?: boolean
    /**
     * Normalized percentage of window's width to be used. Ranges in [0,1], default: 0.8.
     */
    widthPercentage?: number;

    /**
     * Normalized percentage of window's height to be used. Ranges in [0,1], default: 0.8.
     */
    heightPercentage?: number;
    highlights: GaugeHelperHighlight[];
    showTextColor: boolean;
    minValue: number;
    maxValue: number;
    onSave: (newHighlights: GaugeHelperHighlight[]) => void;
    onCancel: () => void;
}
// ------------------------------------------------------
export interface HighlightsEditorState {
    width: number;
    height: number;
    highlights: GaugeHelperHighlight[];
    showErrorDialog: boolean;
    errorMessage: string;
}
// ------------------------------------------------------
export interface GaugeHelperHighlight extends Highlight {
    textColor?: string;
}
// ------------------------------------------------------
export interface IHighlightBounds {
    min: number;
    max: number;
}
// ------------------------------------------------------
export declare type GaugeHelperHighlightDictionary = { [key: string]: GaugeHelperHighlight[] };
// ------------------------------------------------------
export function HighlightJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("HighlightJsonSchemeValidator", {
        title: "SelectDataType",
        type: "array",
        items: {
            type: "object",
            properties: {
                from: {
                    type: "string"
                },
                to: {
                    type: "string"
                },
                color: { type: "string" },
                textColor: { type: "string" },
            },
            required: ["from", "to", "color", "textColor"]
        },
        minItems: 1
    });
}
// ------------------------------------------------------------------------------------------------------
export interface SortableHighlightsTableProps {
    height: number;
    width: number;
    highlights: GaugeHelperHighlight[];
    enableDecimal?: boolean;
    showTextColor: boolean;
    minValue: number;
    maxValue: number;
    onChange: (newHighlights: GaugeHelperHighlight[]) => void;
}
// ------------------------------------------------------------------------------------------------------
export class SortableHighlightsTable extends PureComponent<SortableHighlightsTableProps> {
    // ------------------------------------------------------------------------------------------------------
    static defaultProps: SortableHighlightsTableProps = {
        height: 120,
        width: 120,
        highlights: [],
        maxValue: Number.MAX_SAFE_INTEGER,
        minValue: Number.MIN_SAFE_INTEGER,
        showTextColor: false,
        onChange: _.noop,
        enableDecimal: false
    };
    // ------------------------------------------------------------------------------------------------------
    constructor(props) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------------
    private _onChange(newHighlights: GaugeHelperHighlight[]) {
        if (isFunction(this.props.onChange))
            this.props.onChange(newHighlights);
    }
    // ------------------------------------------------------------------------------------------------------
    private _renderColorPicker(highlight: GaugeHelperHighlight, index: number, forTextColor: boolean): ReactElement<any> {
        return (
            <Popover
                outsideNode={<div
                    style={{
                        padding: '5px',
                        background: '#fff',
                        borderRadius: '1px',
                        boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                        display: 'inline-block',
                        cursor: 'pointer'
                    }}>
                    <div style={{
                        width: '36px',
                        height: '14px',
                        borderRadius: '2px',
                        background: forTextColor ? highlight.textColor : highlight.color
                    }} />
                </div>}
                boxNode={<SketchPicker
                    color={forTextColor ? highlight.textColor : highlight.color}
                    onChange={(color) => {
                        if (forTextColor)
                            this.props.highlights[index].textColor = getStringFromRGBA(color.rgb);
                        else
                            this.props.highlights[index].color = getStringFromRGBA(color.rgb);
                        this._onChange(this.props.highlights);
                    }} />} />
        );
    }
    // ------------------------------------------------------------------------------------------------------
    render() {
        return (
            <div
                style={{
                    maxHeight: this.props.height,
                    width: this.props.width,
                    overflow: 'hidden'
                }}
            >
                <Suspense fallback={<LazyLoadingAnimation />}>
                    <Sortable
                        tableId={'table'}
                        tableHeader={<FlexboxGrid>
                            <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={2}></FlexboxGrid.Item>
                            <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={6}>From</FlexboxGrid.Item>
                            <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={6}>To</FlexboxGrid.Item>
                            <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={4}>Color</FlexboxGrid.Item>
                            <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={4}>Text Color</FlexboxGrid.Item>
                            <FlexboxGrid.Item className='y-flexbox-grid-item table-cell' colspan={2}></FlexboxGrid.Item>
                        </FlexboxGrid>}
                        items={this.props.highlights.map((highlight, index) => {
                            return (
                                <FlexboxGrid>
                                    <FlexboxGrid.Item
                                        key={`sort-${index}`}
                                        className='y-flexbox-grid-item table-cell'
                                        colspan={2}>
                                        <i className='draggable-item fas fa-sort-alt center'
                                            style={{ ...DraggableIconStyle, fontSize: 16, color: grey600 }} />
                                    </FlexboxGrid.Item>

                                    <FlexboxGrid.Item
                                        key={`from-${index}`}
                                        className='y-flexbox-grid-item table-cell'
                                        colspan={2}>
                                        <TextBox
                                            fullWidth
                                            key={'highlight-from' + index}
                                            type='number'
                                            value={highlight.from}
                                            min={this.props.minValue}
                                            max={this.props.maxValue}
                                            errorText={(highlight.from < this.props.minValue ||
                                                highlight.from > this.props.maxValue ||
                                                highlight.from > highlight.to ||
                                                _.isNaN(highlight.from)) ? ' ' : ''}
                                            onChangeString={newValue => {
                                                this.props.highlights[index].from = newValue;
                                                this._onChange(this.props.highlights);
                                            }}
                                        />
                                    </FlexboxGrid.Item>

                                    <FlexboxGrid.Item
                                        key={`to-${index}`}
                                        className='y-flexbox-grid-item table-cell'
                                        colspan={2}>
                                        <TextBox
                                            fullWidth
                                            key={'highlight-to' + index}
                                            type='number'
                                            value={highlight.to}
                                            min={this.props.minValue}
                                            max={this.props.maxValue}
                                            errorText={(highlight.to < this.props.minValue ||
                                                highlight.to > this.props.maxValue ||
                                                highlight.to < highlight.from ||
                                                _.isNaN(highlight.to)) ? ' ' : ''}
                                            onChangeString={newValue => {
                                                this.props.highlights[index].to = newValue;
                                                this._onChange(this.props.highlights);
                                            }}
                                        />
                                    </FlexboxGrid.Item>

                                    <FlexboxGrid.Item
                                        key={`color-${index}`}
                                        className='y-flexbox-grid-item table-cell'
                                        colspan={2}>
                                        {this._renderColorPicker(highlight, index, false)}
                                    </FlexboxGrid.Item>

                                    <FlexboxGrid.Item
                                        key={`text-color-${index}`}
                                        className='y-flexbox-grid-item table-cell'
                                        colspan={2}>
                                        {this.props.showTextColor ? this._renderColorPicker(highlight, index, true) : null}
                                    </FlexboxGrid.Item>

                                    <FlexboxGrid.Item
                                        key={`delete-${index}`}
                                        className='y-flexbox-grid-item table-cell'
                                        colspan={2}>
                                        <IconButton
                                            iconClassName='fas fa-trash'
                                            iconStyle={{ color: grey600 }}
                                            onClick={() => {
                                                this.props.highlights.splice(index, 1)
                                                this._onChange(this.props.highlights);
                                            }} />
                                    </FlexboxGrid.Item>
                                </FlexboxGrid>
                            );
                        })}
                        updateItemsOrder={(oldIndex: number, newIndex: number) => {
                            this._onChange(arrayMoveImmutable(this.props.highlights, oldIndex, newIndex));
                        }}
                    />
                </Suspense>
            </div>
        );
    }
}
// ------------------------------------------------------------------------------------------------------
export interface MultiHighlightsEditorProps {
    isVisible: boolean;
    width: number;
    height: number;
    layer2Highlights: { [key: string]: GaugeHelperHighlight[] };
    showTextColor?: boolean;
    minValue: number;
    maxValue: number;
    enableDecimal: boolean;
    onSave: (newHighlights: { [key: string]: GaugeHelperHighlight[] }) => void;
    onCancel: () => void;
}
// ------------------------------------------------------
export interface MultiHighlightsEditorState {
    layer2Highlights: { [key: string]: GaugeHelperHighlight[] };
    selectedLayerId: string;
    showList: boolean;
    showErrorDialog: boolean;
    errorMessage: ReactElement<any>;
    textFieldValue: string;
    textFieldError: string;
    layerIdToEdit: string;
    showSearchBar: boolean;
    searchKeys: string[];
    emptyValue: boolean;
    SubDialogConf: DialogConf;
    SubDialogConfstyle: CSSProperties;
}
// ------------------------------------------------------
export class MultiHighlightsEditor extends Component<MultiHighlightsEditorProps, MultiHighlightsEditorState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: MultiHighlightsEditorProps) {
        super(props);

        this.state = {
            layer2Highlights: isDefined(this.props.layer2Highlights) ? _.cloneDeep(this.props.layer2Highlights) : {},
            selectedLayerId: '',
            showErrorDialog: false,
            errorMessage: null,
            textFieldValue: '',
            textFieldError: '',
            layerIdToEdit: null,
            showList: true,
            showSearchBar: false,
            emptyValue: true,
            searchKeys: [],
            SubDialogConf: null,
            SubDialogConfstyle: {}
        };

        this._onSave = this._onSave.bind(this);
        this._onCancel = this._onCancel.bind(this);
        this._addOrUpdateLayer = this._addOrUpdateLayer.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: MultiHighlightsEditorProps) {
        // catch highlights from parent
        if (!_.isEqual(this.props.layer2Highlights, prevProps.layer2Highlights)) {
            this.setState({ layer2Highlights: this.props.layer2Highlights });
        }
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const theme = BoardTheming.GetActiveTheme();
        const listWidth = this.props.width > 300 ? 300 : 80;
        const toolbarHeight = this.props.height > theme.genericToolbars.height
            ? theme.genericToolbars.height
            : theme.genericToolbars.height / 2;
        return (
            <div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        height: this.props.height,
                        width: this.props.width,
                        overflow: 'hidden'
                    }}
                >
                    <Toolbar
                        title='Highlights Editor'
                        style={{
                            zIndex: 10000,
                            height: toolbarHeight,
                            width: this.props.width,
                            backgroundColor: "rgb(19, 68, 89)",
                            boxShadow: getZDepthShadow(theme.genericToolbars.style.shadowLevel)
                        }}
                        rightItems={[
                            <Button
                                key='cancel'
                                label='Cancel'
                                height={toolbarHeight}
                                borderRadius={0}
                                shadowLevel={0}
                                style={{ background: 'transparent', color: red200 }}
                                onClick={this._onCancel}
                            />,
                            <Button
                                key='save'
                                label='Save'
                                height={toolbarHeight}
                                borderRadius={0}
                                shadowLevel={0}
                                style={{ background: 'transparent', color: blue200 }}
                                onClick={this._onSave}
                            />
                        ]}
                    />
                    <div style={{ width: this.props.width, height: this.props.height - toolbarHeight }}>
                        {this._renderList(listWidth, this.props.height - toolbarHeight, toolbarHeight)}
                        {this._renderMainView(this.props.width - listWidth, this.props.height - toolbarHeight, toolbarHeight)}
                    </div>
                </div>

                {/* Dialog for saving errors */}
                {this.state.SubDialogConf == null ? null : <ExDialog
                    key={"-dialog-sub"}
                    Enable={this.state.SubDialogConf != null}
                    Conf={this.state.SubDialogConf}
                    style={this.state.SubDialogConfstyle}
                />}
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
    private _onSave() {
        let erroredLayers: string[] = [];
        // validate all layers; keep ids with errors
        _.forEach(this.state.layer2Highlights, (layer, key) => {
            if (!validateHighlights(this.props.minValue, this.props.maxValue, layer)) {
                erroredLayers.push(key);
            }
        });
        if (erroredLayers.length == 0) {
            if (isFunction(this.props.onSave))
                this.props.onSave(this.state.layer2Highlights);
        }
        else {
            this.setState({
                SubDialogConf: {
                    title: "Error",
                    actions: [<Button
                        key='ok'
                        label='OK'
                        borderRadius={0}
                        shadowLevel={0}
                        style={{ background: 'transparent', color: blue200 }}
                        onClick={() => this.setState({ SubDialogConf: null })}
                    />],
                    body: <div style={{ padding: 24 }}>
                        <div>
                            <p>Please check again your <b>'from'</b> and <b>'to'</b> fields which should be included betweeen min and max values.</p>
                            <p>Layers containing errors:</p>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'wrap'
                                }}
                            >
                                {_.map(erroredLayers, (layerId) => {
                                    return <span style={{
                                        margin: 4,
                                        padding: 8,
                                        borderRadius: 15,
                                        backgroundColor: grey300
                                    }}>
                                        {layerId}
                                    </span>
                                })}
                            </div>
                        </div>
                    </div>
                },
                SubDialogConfstyle: { marginTop: 100 }
            });
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _onCancel() {
        if (isFunction(this.props.onCancel))
            this.props.onCancel();
    }
    // ---------------------------------------------------------------------------------------------------
    // todo: apply checks
    private _addOrUpdateLayer() {

        // check for duplicate
        if (isDefined(this.state.layer2Highlights[this.state.textFieldValue])) {
            this.setState({ textFieldError: 'Duplicate layer id' });
            return;
        }

        //ok, procced with add/update
        this.setState((s: MultiHighlightsEditorState) => {
            // check action
            if (isDefined(s.layerIdToEdit)) {
                // get highlights
                const highlights = s.layer2Highlights[s.selectedLayerId];

                // add new entry
                s.layer2Highlights[s.textFieldValue] = highlights;

                // delete old entry
                delete s.layer2Highlights[s.selectedLayerId];
            }
            else {
                s.layer2Highlights[s.textFieldValue] = [{
                    from: this.props.minValue,
                    to: this.props.maxValue,
                    color: 'rgba(0,0,0,1)',
                    textColor: 'rgba(0,0,0,1)'
                }];
            }

            // select this layer
            s.selectedLayerId = s.textFieldValue;

            // empty textField variables
            s.textFieldValue = '';
            s.textFieldError = '';
            s.SubDialogConf = null;
            return s;
        });
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderList(width: number, height: number, toolbarHeight: number): ReactElement<any> {
        const searchbarHeight = !this.state.showSearchBar ? 0 : ((height - toolbarHeight) > 48 ? 48 : 20);
        const layerIds = this.state.emptyValue ? _.keys(this.state.layer2Highlights) : this.state.searchKeys;
        const theme = BoardTheming.GetActiveTheme();
        return (
            <div
                key='list-view'
                style={{
                    height: height,
                    width: width,
                    overflow: 'hidden',
                    borderRight: '1px solid ' + grey300,
                    float: "left"
                }}
            >
                <Toolbar
                    title={null}
                    style={{
                        zIndex: 10000,
                        height: toolbarHeight,
                        position: 'relative',
                        boxShadow: getZDepthShadow(theme.genericToolbars.style.shadowLevel),
                        color: theme.genericToolbars.style.fontColor,
                        backgroundColor: theme.genericToolbars.style.backgroundColor
                    }}
                    rightItems={[
                        <IconButton
                            key='add-layer-icon'
                            tooltip='Add new layer'
                            iconClassName='fas fa-plus'
                            iconStyle={{ display: this.state.showList ? 'initial' : 'none' }}
                            onClick={() => this.setState({
                                SubDialogConfstyle: { marginTop: 100 },
                                SubDialogConf: this._getUpsertLayerDialogConf(toolbarHeight)
                            })}
                        />,
                        <IconButton
                            key='toggle-searchbar-icon'
                            iconClassName='fas fa-search'
                            iconStyle={{ display: this.state.showList ? 'initial' : 'none' }}
                            onClick={() => this.setState({ showSearchBar: !this.state.showSearchBar })}
                        />,
                        <IconButton
                            key='toggle-list-icon'
                            iconClassName={this.state.showList ? 'fas fa-chevron-left' : 'fas fa-chevron-right'}
                            onClick={() => this.setState({ showList: !this.state.showList })}
                        />
                    ]}
                />
                <div style={{
                    width: width,
                    height: searchbarHeight,
                    overflow: 'hidden',
                    padding: 0
                }}>
                    <SearchBar
                        simple
                        autoFocus
                        key={'search-bar'}
                        style={{
                            maxWidth: width,
                            backgroundColor: theme.genericToolbars.style.subBackgroundColor,
                            borderRadius: undefined
                        }}
                        source={_.map(this.state.layer2Highlights, (layer, key) => {
                            return { key: key, value: key };
                        })}
                        onSearch={(keys, emptySearchValue) => {
                            this.setState({
                                searchKeys: isDefined(keys) ? keys : [],
                                emptyValue: emptySearchValue
                            });
                        }}
                    />
                </div>
                {layerIds.length == 0
                    ? <div>
                        <p style={{ textAlign: 'center', padding: 5 }}>
                            {this.state.emptyValue ? 'No layers' : 'No matching layers found'}
                        </p>
                    </div>
                    : <div
                        style={{
                            height: height - toolbarHeight - searchbarHeight,
                            width: width,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            padding: 0
                        }}
                    >
                        {_.map(layerIds, layerId => {
                            return <ListItem
                                key={layerId}
                                primaryText={layerId}
                                style={this.state.selectedLayerId === layerId
                                    ? { backgroundColor: grey300 } : {}}
                                onClick={() => this.setState({ selectedLayerId: layerId })}
                                rightIcon={ToolbarIconInfo_ButtonRender([{
                                    id: 'right-menu',
                                    type: 'icon',
                                    icon: 'fas fa-ellipsis-v center',
                                    iconType: eIconType.fontAwesome,
                                    iconColor: grey500,
                                    onIconClickDropdown: true,
                                    priv: _.noop,
                                    nestedItems: [{
                                        id: 'edit',
                                        type: 'icon',
                                        label: 'Edit layer',
                                        icon: 'fas fa-pen center',
                                        iconType: eIconType.fontAwesome,
                                        iconColor: grey500,
                                        priv: () => this.setState({
                                            SubDialogConfstyle: { marginTop: 100 },
                                            SubDialogConf: this._getUpsertLayerDialogConf(toolbarHeight),
                                            layerIdToEdit: layerId,
                                            textFieldValue: layerId,
                                            textFieldError: ''
                                        })
                                    },
                                    {
                                        id: 'delete',
                                        type: 'icon',
                                        label: 'Delete layer',
                                        icon: 'fas fa-trash center',
                                        iconType: eIconType.fontAwesome,
                                        iconColor: red200,
                                        priv: () => this.setState((s: MultiHighlightsEditorState) => {
                                            // if deleting selected layer; deselect
                                            if (s.selectedLayerId == layerId)
                                                s.selectedLayerId = '';
                                            delete s.layer2Highlights[layerId];
                                            return s;
                                        })
                                    }]
                                }], [], [], {}, x => x.priv())}
                            />
                        })}
                    </div>
                }
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
    private _getUpsertLayerDialogConf = (toolbarHeight: number) => {
        return {
            title: isDefined(this.state.layerIdToEdit) ? 'Edit layer' : 'Add layer',
            actions: [
                <Button
                    key='cancel'
                    label='Cancel'
                    height={toolbarHeight}
                    borderRadius={0}
                    shadowLevel={0}
                    style={{ background: 'transparent', color: red200 }}
                    onClick={() => this.setState({ SubDialogConf: null })}
                />,
                <Button
                    key='save'
                    label={isDefined(this.state.layerIdToEdit) ? 'Save' : 'Add'}
                    height={toolbarHeight}
                    borderRadius={0}
                    shadowLevel={0}
                    style={{ background: 'transparent', color: blue200 }}
                    onClick={() => this._addOrUpdateLayer()}
                />
            ],
            body: <div style={{ padding: 24 }}>
                <TextBox
                    key='layer-id-textfield'
                    type='text'
                    prefix='Name'
                    errorText={this.state.textFieldError}
                    value={this.state.textFieldValue}
                    onEnterPress={() => this._addOrUpdateLayer()}
                    onChangeString={newValue => this.setState({
                        textFieldValue: newValue,
                        textFieldError: ''
                    })}
                />
            </div>
        } as DialogConf;
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderMainView(width: number, height: number, toolbarHeight: number): ReactElement<any> {
        const theme = BoardTheming.GetActiveTheme();
        return (
            <div
                key='main-view'
                style={{
                    height: height,
                    width: width,
                    overflow: 'hidden',
                    float: "right"
                }}
            >
                <Toolbar
                    title={IsNullOrEmpty(this.state.selectedLayerId)
                        ? 'Highlights' : ('Highlights: ' + this.state.selectedLayerId)}
                    style={{
                        zIndex: 10000,
                        height: toolbarHeight,
                        position: 'relative',
                        boxShadow: getZDepthShadow(theme.genericToolbars.style.shadowLevel),
                        color: theme.genericToolbars.style.fontColor,
                        backgroundColor: theme.genericToolbars.style.backgroundColor
                    }}
                    rightItems={[
                        <IconButton
                            key='add-new-highlight-icon-button'
                            tooltip='Add new Highlight'
                            iconClassName='fas fa-plus'
                            iconStyle={{ display: IsNullOrEmpty(this.state.selectedLayerId) ? 'none' : 'initial' }}
                            onClick={() => {
                                this.setState((s) => {
                                    if (s.layer2Highlights[s.selectedLayerId] == null)
                                        s.layer2Highlights[s.selectedLayerId] = [];

                                    s.layer2Highlights[s.selectedLayerId].push({
                                        from: this.props.minValue,
                                        to: this.props.maxValue,
                                        color: 'rgba(0,0,0,1)',
                                        textColor: 'rgba(0,0,0,1)'
                                    });
                                    return s;
                                });
                            }}
                        />
                    ]}
                />
                {IsNullOrEmpty(this.state.selectedLayerId) ?
                    <div
                        key={"paper-empty"}
                        className="noselect"
                        style={{
                            width: (width) / 2,
                            height: (height - toolbarHeight) / 2,
                            textAlign: 'center',
                            margin: '20px auto',
                            fontSize: 'x-large',
                            fontWeight: 700,
                            boxShadow: getZDepthShadow(1)
                        }}>
                        <p style={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>Please select a layer</p>
                    </div>
                    : <SortableHighlightsTable
                        key={this.state.selectedLayerId}
                        height={height - toolbarHeight}
                        width={width}
                        highlights={this.state.layer2Highlights[this.state.selectedLayerId]}
                        onChange={(newHighlights) => {
                            this.setState((s) => {
                                s.layer2Highlights[s.selectedLayerId] = newHighlights;
                                return s;
                            });
                        }}
                        minValue={this.props.minValue}
                        maxValue={this.props.maxValue}
                        showTextColor={this.props.showTextColor}
                        enableDecimal={this.props.enableDecimal}
                    />}
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
}